/* oxlint-disable sorare/enforce-monetary-reward-disclaimer -- Non-reward currency (wallet/market/JONUM limits/etc.) — disclaimer does not apply. */
import { useCallback } from 'react';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useCryptoAmount } from '@sorare/core/src/hooks/useCryptoAmount';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';

const algoliaWeiFactor = BigInt(1e13);

type ReturnedProps = {
  algoliaWeiFactor: bigint;
  formatPrice: (value: number, maxReachesEnd?: boolean) => string;
  sliderToPrice: (value: number) => string;
  priceToSlider: (value: number) => number;
};

export const useFormatFilters = (): ReturnedProps => {
  const { currency, fiatCurrency, cryptocurrency } = useCurrentUserCurrencies();
  const { wei, getUserAmount, toMonetaryAmountFromMajorMainCurrency } =
    useMonetaryAmount();
  const { formatCryptoMajor } = useCryptoAmount(cryptocurrency);
  const { formatNumber } = useIntlContext();

  const sliderToPrice = useCallback(
    (value: number): string => {
      const monetaryAmount = wei(Math.round(value * Number(algoliaWeiFactor)));
      return getUserAmount(monetaryAmount).toFixed(2);
    },
    [wei, getUserAmount]
  );

  const formatPrice = useCallback(
    (value: number, maxReachesEnd?: boolean) => {
      const price = sliderToPrice(value);

      const formattedNumber = (() => {
        if (currency === Currency.FIAT) {
          return formatNumber(Number(price), {
            style: 'currency',
            currency: fiatCurrency,
          });
        }
        return formatCryptoMajor(price);
      })();
      return `${formattedNumber}${maxReachesEnd ? '+' : ''}`;
    },
    [currency, fiatCurrency, formatCryptoMajor, formatNumber, sliderToPrice]
  );

  const priceToSlider = useCallback(
    (value: number): number => {
      return Number(
        toMonetaryAmountFromMajorMainCurrency(value).wei / algoliaWeiFactor
      );
    },
    [toMonetaryAmountFromMajorMainCurrency]
  );

  return {
    algoliaWeiFactor,
    formatPrice,
    sliderToPrice,
    priceToSlider,
  };
};
